/**
 * Returns the singular or plural form of a word based on count
 * Similar to Rails' pluralize helper
 *
 * @param count - The number to determine singular/plural
 * @param singular - The singular form of the word
 * @param plural - Optional plural form (defaults to singular + 's')
 * @returns The count followed by the appropriate form of the word
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  const pluralForm = plural || `${singular}s`
  const word = count === 1 ? singular : pluralForm
  return `${count} ${word}`
}

/**
 * Converts a string to title case (capitalizes each word)
 * Similar to Rails' titleize helper
 *
 * @param text - The text to convert to title case
 * @returns The text with each word capitalized
 */
export function titleize(text: string): string {
  if (!text) return ""

  return text
    .toLowerCase()
    .split(/[\s_-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

/**
 * Converts a string to human-readable format by replacing underscores/dashes with spaces
 * and capitalizing the first letter. Similar to Rails' humanize helper
 *
 * @param text - The text to humanize
 * @returns The humanized text
 */
export function humanize(text: string): string {
  if (!text) return ""

  const humanized = text.toLowerCase().replace(/[_-]/g, " ").trim()

  return humanized.charAt(0).toUpperCase() + humanized.slice(1)
}

/**
 * Converts literal escape sequences (the two characters "\" + "n", "\" + "t", etc.)
 * into their real whitespace characters. Some providers (e.g. Zoom Phone) deliver SMS
 * bodies with escaped newlines/tabs rather than the actual characters, which would
 * otherwise render to the user as a literal "\n".
 *
 * Pair with `whitespace-pre-wrap` so the resulting newlines and tabs are honored.
 *
 * @param text - The text that may contain literal escape sequences
 * @returns The text with \r\n, \n, \r, and \t converted to real characters
 */
export function unescapeWhitespace(text: string): string {
  return text.replace(/\\r\\n|\\n|\\r|\\t/g, (match) => (match === "\\t" ? "\t" : "\n"))
}

/**
 * Extracts the first word/name from a string
 * Handles edge cases like empty strings, undefined values, and extra whitespace
 *
 * @param text - The text to extract the first word from
 * @returns The first word, or an empty string if no valid word exists
 */
export function firstWord(text: string | undefined | null): string {
  if (!text) return ""

  // Trim whitespace and split on any whitespace characters
  const words = text.trim().split(/\s+/)
  return words[0] || ""
}
